import { Injectable } from '@angular/core';
import { SharedService } from '../../shared/shared.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ProjectResponse,
  ProjectWithStatsResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  GrantProjectAccessRequest,
  UpdateProjectAccessRequest,
  ProjectAccessResponse,
  ProjectAccessListResponse,
  UserProjectAccessResponse,
  ProjectOption
} from '../models/project.models';

@Injectable({
  providedIn: 'root'
})
export class TaskProjectService {
  private baseUrl = `${environment.fastApiUrl}/api/task-projects`;

  constructor(private sharedService: SharedService) { }

  // ==================== Projects ====================

  /**
   * Get all projects
   */
  getProjects(groupId?: number, isActive?: boolean): Observable<ProjectResponse[]> {
    let url = `${this.baseUrl}?`;
    const params: string[] = [];
    
    if (groupId) params.push(`group_id=${groupId}`);
    if (isActive !== undefined) params.push(`is_active=${isActive}`);
    
    url += params.join('&');
    return this.sharedService.getFastApiData(url);
  }

  /**
   * Get project by ID with statistics
   */
  getProject(projectId: number): Observable<ProjectWithStatsResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${projectId}`);
  }

  /**
   * Create project
   */
  createProject(request: CreateProjectRequest): Observable<any> {
    return this.sharedService.postFastApiDataWithHeaders(this.baseUrl, request).pipe(
      map(response => response.body)
    );
  }

  /**
   * Update project
   */
  updateProject(projectId: number, request: UpdateProjectRequest): Observable<any> {
    return this.sharedService.putFastApiData(`${this.baseUrl}/${projectId}`, request).pipe(
      map(response => response.body)
    );
  }

  /**
   * Delete project
   */
  deleteProject(projectId: number): Observable<any> {
    return this.sharedService.deleteFastApiData(`${this.baseUrl}/${projectId}`).pipe(
      map(response => response.body)
    );
  }

  /**
   * Close project
   */
  closeProject(projectId: number): Observable<any> {
    return this.sharedService.postFastApiDataWithHeaders(`${this.baseUrl}/${projectId}/close`, {}).pipe(
      map(response => response.body)
    );
  }

  /**
   * Reopen project
   */
  reopenProject(projectId: number): Observable<any> {
    return this.sharedService.postFastApiDataWithHeaders(`${this.baseUrl}/${projectId}/reopen`, {}).pipe(
      map(response => response.body)
    );
  }

  // ==================== Project Access Control ====================

  /**
   * Grant project access to user or group
   */
  grantProjectAccess(projectId: number, request: GrantProjectAccessRequest): Observable<any> {
    return this.sharedService.postFastApiDataWithHeaders(`${this.baseUrl}/${projectId}/access`, request).pipe(
      map(response => response.body)
    );
  }

  /**
   * Update project access role
   */
  updateProjectAccess(projectId: number, accessId: number, request: UpdateProjectAccessRequest): Observable<any> {
    return this.sharedService.putFastApiData(`${this.baseUrl}/${projectId}/access/${accessId}`, request).pipe(
      map(response => response.body)
    );
  }

  /**
   * Revoke project access
   */
  revokeProjectAccess(projectId: number, accessId: number): Observable<any> {
    return this.sharedService.deleteFastApiData(`${this.baseUrl}/${projectId}/access/${accessId}`).pipe(
      map(response => response.body)
    );
  }

  /**
   * List all access grants for a project
   */
  getProjectAccess(projectId: number): Observable<ProjectAccessListResponse> {
    return this.sharedService.getFastApiData(`${this.baseUrl}/${projectId}/access`);
  }

  /**
   * Get all projects accessible to current user
   */
  getMyAccessibleProjects(): Observable<UserProjectAccessResponse> {
    return this.sharedService.getFastApiData(`${environment.fastApiUrl}/api/users/me/accessible-projects`);
  }

  // ==================== Project Tasks ====================

  /**
   * Get all tasks in a project
   */
  getProjectTasks(projectId: number, isClosed?: boolean): Observable<any> {
    let url = `${this.baseUrl}/${projectId}/tasks`;
    if (isClosed !== undefined) {
      url += `?is_closed=${isClosed}`;
    }
    return this.sharedService.getFastApiData(url);
  }

  // ==================== Helper Methods ====================

  /**
   * Convert projects to dropdown options
   */
  mapProjectsToOptions(projects: ProjectResponse[]): ProjectOption[] {
    return projects.map(project => ({
      value: project.id,
      label: project.name,
      color: project.color,
      isActive: project.is_active
    }));
  }

  /**
   * Calculate project completion percentage
   */
  calculateCompletionRate(project: ProjectWithStatsResponse): number {
    if (project.total_tasks === 0) return 0;
    return Math.round((project.completed_tasks / project.total_tasks) * 100);
  }

  /**
   * Check if project is at risk (has overdue tasks)
   */
  isProjectAtRisk(project: ProjectWithStatsResponse): boolean {
    return project.overdue_tasks > 0;
  }
}
